import React, { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { firebaseProjectId } from '../../../utils/firebase';
import { Logo } from '../../Logo';
import { Lock, Mail, ArrowRight, AlertCircle, RefreshCw, Eye } from 'lucide-react';
function DiagnosticRow({
  label,
  value,
  mono
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return <div className="flex items-start justify-between gap-4 px-4 py-3" data-id="element-187" data-mp="1zzznrfrkgpfm">
      <dt className="text-[#5A5A5A] shrink-0" data-id="element-188" data-mp="24vnazpmhn8ih">{label}</dt>
      <dd className={`text-right text-[#111111] font-medium break-all ${mono ? 'font-mono text-xs' : ''}`} data-id="element-189" data-mp="28ytguw5ouxof">
        {value}
      </dd>
    </div>;
}
export function AdminLogin() {
  const {
    user,
    profile,
    loading,
    profileError,
    signIn,
    signOut
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const redirectTo = (location.state as {
    from?: Location;
  })?.from?.pathname || '/admin';
  if (loading) {
    return <div className="min-h-screen bg-[#F9F9F9] flex items-center justify-center" data-id="element-190" data-mp="32igpsmryonj5">
        <span className="w-6 h-6 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" data-id="element-191" data-mp="2xn1eo2lc95qf" />
      </div>;
  }
  if (user && profile?.role === 'admin') {
    return <Navigate to={redirectTo} replace data-id="element-192" data-mp="3qb6vwfd6ocli" />;
  }
  if (user && profile?.role !== 'admin') {
    return <div className="min-h-screen bg-[#F9F9F9] flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 font-sans" data-id="element-193" data-mp="3l9rcerqnme58">
        <div className="sm:mx-auto sm:w-full sm:max-w-lg" data-id="element-194" data-mp="3gbpuwb8vmmvv">
          <div className="text-center" data-id="element-195" data-mp="29w5qdfg3csnx">
            <Logo className="w-12 h-12 mx-auto mb-4" data-id="element-196" data-mp="29x71fjx3xf5n" />
            <h2 className="text-3xl font-bold text-[#111111] tracking-tight" data-id="element-197" data-mp="24w9ih549wh33">
              Admin access not granted
            </h2>
            <p className="mt-2 text-sm text-[#5A5A5A]" data-id="element-198" data-mp="26ehmunlpc7zk">
              You are signed in, but this account did not resolve to an admin
              role. The details below show exactly what the app sees.
            </p>
          </div>

          <div className="mt-8 bg-white border border-[#E5E5E5] rounded-xl overflow-hidden" data-id="element-199" data-mp="22ccwpyugptho">
            <dl className="divide-y divide-[#E5E5E5] text-sm" data-id="element-200" data-mp="22e7kdkx4bzpg">
              <DiagnosticRow label="Signed in as" value={user.email ?? '—'} data-id="element-201" data-mp="1nlxb4iy4wjiz" />
              <DiagnosticRow label="Account UID" value={user.uid} mono data-id="element-202" data-mp="1sf8korubzeel" />
              <DiagnosticRow label="Firebase project" value={firebaseProjectId} mono data-id="element-203" data-mp="1en263a6ttfh9" />
              <DiagnosticRow label="Profile document" value={profile ? 'Found' : 'Not found'} data-id="element-204" data-mp="1iox2q1dtb4op" />
              <DiagnosticRow label="Role value" value={profile?.role ? `"${profile.role}"` : '—'} mono data-id="element-205" data-mp="19npg4gknrsag" />
            </dl>
          </div>

          <div className="mt-4 bg-white border-l-4 border-burgundy border-y border-r border-y-[#E5E5E5] border-r-[#E5E5E5] p-4 flex items-start gap-3" data-id="element-206" data-mp="l2uatm1hbotx">
            <AlertCircle className="w-5 h-5 text-burgundy shrink-0 mt-0.5" data-id="element-207" data-mp="101o9z723y81g" />
            <div className="text-sm text-[#5A5A5A]" data-id="element-208" data-mp="v0qq8q1g95xu">
              <p className="font-bold text-[#111111] mb-1" data-id="element-209" data-mp="v3cyyt02trhn">Likely cause</p>
              <p data-id="element-210" data-mp="18yylmtiu7z6w">
                {profileError ?? 'A profile was found, but its role field is not exactly "admin".'}
              </p>
              <p className="mt-2" data-id="element-211" data-mp="1ir4a3t9vryhc">
                Confirm the document ID in <strong data-id="element-212" data-mp="1isnvtzeb31qi">customers</strong> matches
                the Account UID above, and that it lives in project{' '}
                <strong data-id="element-213" data-mp="1rqzsai1ustqv">{firebaseProjectId}</strong>.
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3" data-id="element-214" data-mp="1npxrhwsag9ms">
            <button onClick={() => window.location.reload()} className="flex-1 flex justify-center items-center gap-2 py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-bold text-white bg-tealslate hover:bg-navyslate focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-tealslate transition-colors" data-id="element-215" data-mp="1nnl0yv8gsqy4">
              <RefreshCw className="w-4 h-4" data-id="element-216" data-mp="3vvzncu38keqa" />
              Retry
            </button>
            <button onClick={() => signOut()} className="flex-1 flex justify-center items-center gap-2 py-3 px-4 border border-[#E5E5E5] rounded-md shadow-sm text-sm font-bold text-[#111111] bg-white hover:bg-[#F9F9F9] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-tealslate transition-colors" data-id="element-217" data-mp="2yakcepgyya1k">
              Sign Out
            </button>
          </div>

          <button type="button" onClick={() => navigate('/admin?preview=true', {
          replace: true
        })} className="mt-3 w-full flex justify-center items-center gap-2 py-3 px-4 rounded-md text-sm font-bold text-[#5A5A5A] hover:text-tealslate focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-tealslate transition-colors" data-id="element-218" data-mp="ngpqc0e7dzpd">
            <Eye className="w-4 h-4" data-id="element-219" data-mp="236gtyqhgakjm" />
            Continue to admin in preview mode
          </button>
        </div>
      </div>;
  }
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email, password);
      // The auth context will update and the component will re-render,
      // hitting the Navigate block above if they are an admin.
      // If they are not an admin, they will be redirected to /admin by the Navigate block,
      // but AdminLayout will catch them and redirect back here.
      // To handle non-admins gracefully, we can just navigate to /admin and let AdminLayout handle it,
      // or we can wait for the profile to load.
      navigate(redirectTo, {
        replace: true
      });
    } catch (err: any) {
      setError(err.message || 'Failed to sign in');
      setSubmitting(false);
    }
  };
  return <div className="min-h-screen bg-[#F9F9F9] flex flex-col justify-center py-12 sm:px-6 lg:px-8 font-sans" data-id="element-220" data-mp="3jt2ijbbhknwa">
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center" data-id="element-221" data-mp="3ordrzkxvcwdz">
        <Logo className="w-12 h-12 mx-auto mb-4" data-id="element-222" data-mp="3opj2rufkei43" />
        <h2 className="text-3xl font-bold text-[#111111] tracking-tight" data-id="element-223" data-mp="gd52ilqn8lq1">
          Admin Portal
        </h2>
        <p className="mt-2 text-sm text-[#5A5A5A]" data-id="element-224" data-mp="kd5iiate54z7">
          Sign in to manage orders and newsletter subscribers.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md" data-id="element-225" data-mp="bd0msxxk7v0v">
        <div className="bg-white py-8 px-4 shadow-sm border border-[#E5E5E5] sm:rounded-xl sm:px-10" data-id="element-226" data-mp="bbftfbmzei4l">
          <form className="space-y-6" onSubmit={handleSubmit} data-id="element-227" data-mp="2r2k79n0lzg7s">
            <div data-id="element-228" data-mp="2w3hwukny7cjb">
              <label htmlFor="email" className="block text-sm font-bold text-[#111111] mb-2" data-id="element-229" data-mp="2w41519nsqmk5">
                Email address
              </label>
              <div className="relative" data-id="element-230" data-mp="2jjkonjkz1sph">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-231" data-mp="2jmypgndzltut">
                  <Mail className="h-5 w-5 text-[#9A9A90]" data-id="element-232" data-mp="2xi12rvl5ylhn" />
                </div>
                <input id="email" name="email" type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} className="block w-full pl-10 pr-3 py-3 border border-[#E5E5E5] rounded-md text-[#111111] placeholder-[#9A9A90] focus:outline-none focus:ring-1 focus:ring-tealslate focus:border-tealslate sm:text-sm transition-colors" placeholder="admin@example.com" data-id="element-233" data-mp="2sm3qc0d0tza5" />
              </div>
            </div>

            <div data-id="element-234" data-mp="1h4ax4keebp5w">
              <label htmlFor="password" className="block text-sm font-bold text-[#111111] mb-2" data-id="element-235" data-mp="wklbh09fnqc0">
                Password
              </label>
              <div className="relative" data-id="element-236" data-mp="15l01ue197vmd">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-237" data-mp="15jyr6ao5hb56">
                  <Lock className="h-5 w-5 text-[#9A9A90]" data-id="element-238" data-mp="1akw5ud9ybn1x" />
                </div>
                <input id="password" name="password" type="password" autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)} className="block w-full pl-10 pr-3 py-3 border border-[#E5E5E5] rounded-md text-[#111111] placeholder-[#9A9A90] focus:outline-none focus:ring-1 focus:ring-tealslate focus:border-tealslate sm:text-sm transition-colors" placeholder="••••••••" data-id="element-239" data-mp="1fjzahwm0rr9d" />
              </div>
            </div>

            {error && <div className="bg-burgundy/5 border-l-4 border-burgundy p-4 flex items-start gap-3" data-id="element-240" data-mp="3nogbt4ankdf0">
                <AlertCircle className="w-5 h-5 text-burgundy shrink-0 mt-0.5" data-id="element-241" data-mp="3iu2m9iio90i9" />
                <p className="text-sm text-burgundy" data-id="element-242" data-mp="3dsvbrbp59n7w">{error}</p>
              </div>}

            <button type="submit" disabled={submitting} className="w-full flex justify-center items-center gap-2 py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-bold text-white bg-tealslate hover:bg-navyslate focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-tealslate transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-243" data-mp="38vd2fk77t5zd">
              {submitting ? <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-244" data-mp="t2nu1j79woyb" /> : <>
                  Sign in to Admin <ArrowRight className="w-4 h-4" data-id="element-245" data-mp="2863msxfc6zh" />
                </>}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-[#E5E5E5]" data-id="element-246" data-mp="3ucgqdd15qzst">
            <button type="button" onClick={() => navigate('/admin?preview=true', {
            replace: true
          })} className="w-full flex justify-center items-center gap-2 py-3 px-4 border border-[#E5E5E5] rounded-md text-sm font-bold text-[#5A5A5A] bg-white hover:border-tealslate hover:text-tealslate focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-tealslate transition-colors" data-id="element-247" data-mp="3uebaip4lunvu">
              <Eye className="w-4 h-4" data-id="element-248" data-mp="pxqlf8hucy76" />
              Preview admin without signing in
            </button>
            <p className="mt-3 text-xs text-center text-[#9A9A90] leading-relaxed" data-id="element-249" data-mp="14v0wwhuhi42w">
              Opens the admin panel in preview mode for design review. Live
              order and subscriber data may be unavailable.
            </p>
          </div>
        </div>
      </div>
    </div>;
}